import { useEffect, useRef, useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { Toggle } from './toggle'
import { NumericInput } from './numeric-input'
import { setHoldingsFilter, useHoldingsFilter } from '@/lib/holdings-filter'

/** The control on the holdings header: hide balances under a dollar line (John, Oct 30 2026). */
export function HoldingsSettings() {
  const filter = useHoldingsFilter()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(String(filter.minUsd))
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])
  function toggle() {
    if (!open) setDraft(String(filter.minUsd))
    setOpen(!open)
  }
  function edit(value: string) {
    setDraft(value)
    const amount = Number(value)
    if (value.trim() !== '' && Number.isFinite(amount) && amount >= 0) setHoldingsFilter({ minUsd: amount })
  }
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="btn btn-ghost !p-2"
        title="Holdings settings"
        aria-label="Holdings settings"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        <SlidersHorizontal className="size-4" />
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Holdings settings"
          className="card absolute right-0 top-full z-40 mt-2 w-72 max-w-[calc(100vw-2rem)] p-4 shadow-lg shadow-black/40"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium">Hide small balances</div>
              <div className="text-xs text-muted">Tokens worth less than this stay out of the list</div>
            </div>
            <Toggle checked={filter.hideSmall} onChange={(hideSmall) => setHoldingsFilter({ hideSmall })} label="Hide small balances" />
          </div>
          <label className={`mt-4 block ${filter.hideSmall ? '' : 'opacity-50'}`}>
            <span className="text-xs text-muted">Hide under</span>
            <span className="mt-1 flex items-center gap-1 rounded-xl bg-surface-2 px-3">
              <span className="text-muted">$</span>
              <NumericInput
                value={draft}
                disabled={!filter.hideSmall}
                onChange={edit}
                className="w-full bg-transparent py-2 tabular-nums outline-none"
              />
            </span>
          </label>
        </div>
      ) : null}
    </div>
  )
}
